/**
 * Where a design is kept: in the account, or - for a visitor - in the browser.
 *
 * Outside React because more than one thing needs it. Saving is the header's
 * business, but adding a picture needs a design to file it against too, and so
 * does leaving for the basket; all three go through ensureStored() so that a
 * design is created in the account once, on first need, and never twice.
 */

import { api, getConfig } from '../api';
import { registerAssets, type Asset } from '../assets';
import type { Design } from '../model/types';
import { draftKey, dropDraft, forgetPicture, keptFile, localIds, withUploads, writeDraft, type Draft } from '../local';
import { useEditor } from './store';

/** This card's draft, in the visitor's browser. */
export function currentDraftKey(): string {
  const config = getConfig();

  return draftKey(config.product.id, config.product.variation);
}

/**
 * Writes the visitor's design into their browser.
 *
 * @returns false when the browser refused - storage full, or turned off.
 */
export function keepLocally(): boolean {
  const state = useEditor.getState();

  if (!state.doc) return false;

  return writeDraft(currentDraftKey(), {
    doc: state.doc,
    title: state.design?.title ?? '',
    at: Date.now(),
  });
}

let creating: Promise<string> | null = null;

/**
 * The design's uuid, creating it in the account first if it has none.
 *
 * A signed-in customer's new card is not stored until there is something worth
 * storing - its first save, or its first picture. Concurrent callers share one
 * request, so an autosave and an upload landing together make one design.
 */
export function ensureStored(): Promise<string> {
  const state = useEditor.getState();

  if (state.design?.uuid) return Promise.resolve(state.design.uuid);

  /*
   * No sign-in test here any more: the server decides. A customer's design is
   * stored against their account; a guest's is stored against the basket it was
   * made for, and only once they have asked to buy it. Either way this is the
   * one place a design is created, so it is created once.
   */
  if (!state.doc) return Promise.reject(new Error('There is no design to save yet.'));

  if (!creating) {
    const doc = state.doc;
    const title = state.design?.title ?? '';

    creating = api
      .create(doc, title || undefined)
      .then((created) => {
        useEditor.setState((now) => ({
          // The document stays the one on screen; the server's copy is the
          // same thing, normalised, and swapping it in would reset the canvas.
          design: { ...created, title: now.design?.title || created.title, document: now.doc ?? doc },
        }));

        if (useEditor.getState().doc === doc) useEditor.getState().markSaved();

        rememberInAddress(created.uuid);

        return created.uuid;
      })
      .finally(() => {
        creating = null;
      });
  }

  return creating;
}

/**
 * Puts the design on the editor's own address, so a reload reopens it.
 *
 * Without this a customer who refreshed the page after their first save would
 * get a blank card - and a second design in their list - rather than their own.
 */
function rememberInAddress(uuid: string) {
  try {
    const url = new URL(window.location.href);

    url.searchParams.set('design', uuid);
    url.searchParams.delete('resume');

    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // An address that cannot be rewritten only costs the reload behaviour.
  }
}

/** Takes `resume` off the address once it has been acted on. */
export function forgetResume() {
  try {
    const url = new URL(window.location.href);

    if (!url.searchParams.has('resume')) return;

    url.searchParams.delete('resume');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Harmless: resume with nothing to resume does nothing.
  }
}

/**
 * Moves a visitor's design from their browser into the account they just
 * signed into: created there, its pictures uploaded to it, its layers pointed
 * at the uploads, and the browser's copy removed.
 *
 * @param onProgress Told how many pictures are left, for the header to say.
 */
export async function moveIntoAccount(draft: Draft, onProgress?: (left: number) => void): Promise<Design> {
  const config = getConfig();

  // The card being designed now, whatever the draft says - the variation could
  // have been chosen differently on the way back.
  const doc = { ...draft.doc, product: { id: config.product.id, variation: config.product.variation } };

  const created = await api.create(doc, draft.title || undefined);
  const ids = localIds(doc);
  const uploads = new Map<string, Asset>();

  for (let i = 0; i < ids.length; i++) {
    onProgress?.(ids.length - i);

    const file = await keptFile(ids[i]!);

    if (!file) continue;

    try {
      const asset = await api.upload(created.uuid, file);

      uploads.set(ids[i]!, asset);
    } catch {
      // Left pointing at the kept copy, which still draws here; the preflight
      // reports it, and the customer can place the picture again.
    }
  }

  const placed = withUploads(doc, uploads);
  const saved = uploads.size ? await api.save(created.uuid, placed, {}) : created;

  registerAssets(Array.from(uploads.values()));

  for (const id of uploads.keys()) void forgetPicture(id);

  dropDraft(currentDraftKey());
  rememberInAddress(created.uuid);

  return { ...saved, title: saved.title, document: placed };
}

/**
 * Everything a design needs before it can be bought.
 *
 * The design itself, and the pictures in it. A signed-in customer's pictures
 * went up as they were placed, so usually there is nothing left to send; a
 * guest's stayed in their own browser until this moment, because until now
 * there was no basket to keep them for.
 *
 * The uploaded ones are swapped into the document straight away rather than
 * through a commit: the customer did not make this change and should not be
 * able to undo it - the picture is the same picture, now somewhere the press
 * can reach it.
 *
 * A picture that will not go is a refusal, not a warning.
 *
 * This used to carry on and leave the layer pointing at the browser's copy, on
 * the reasoning that losing an order over one logo was worse. It is not: the
 * picture still draws on the customer's own screen and still appears on their
 * proof, so they approve a card that the press will print without it. The
 * customer finds out when a thousand of them arrive. Stopping here, while they
 * are still in the editor and can try again, is the kinder end of that.
 */
export async function commitForBasket(): Promise<string> {
  const uuid = await ensureStored();
  const doc = useEditor.getState().doc;

  if (!doc) return uuid;

  const ids = localIds(doc);
  const uploads = new Map<string, Asset>();

  for (const id of ids) {
    const file = await keptFile(id);

    if (!file) {
      throw new Error(
        'One of the pictures in this design is no longer in your browser. Please place it again before ordering.'
      );
    }

    try {
      uploads.set(id, await api.upload(uuid, file));
    } catch (error) {
      throw new Error(
        `A picture in this design could not be sent to the shop, so it would not be printed: ${
          error instanceof Error ? error.message : 'please try again'
        }`
      );
    }
  }

  if (uploads.size) {
    const placed = withUploads(doc, uploads);

    registerAssets(Array.from(uploads.values()));
    useEditor.setState({ doc: placed });

    for (const id of uploads.keys()) void forgetPicture(id);
  }

  // The browser's copy has done its job: the design is the shop's now, and two
  // copies that can drift apart are worse than one.
  dropDraft(currentDraftKey());

  return uuid;
}
